import React, { useState } from 'react';
import { bool, func, object } from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import {
  ensureCurrentUser,
  ensureStripeCustomer,
  ensurePaymentMethodCard,
} from '../../../util/data';
import { propTypes } from '../../../util/types';
import { savePaymentMethod, deletePaymentMethod } from '../../../ducks/paymentMethods.duck';
import { handleCardSetup } from '../../../ducks/stripe.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../../ducks/ui.duck';
import {
  H3,
  SavedCardDetails,
  PrimaryButton,
  SecondaryButton,
  IconSpinner,
} from '../../../components';

import PaymentMethodsForm from '../../PaymentMethodsPage/PaymentMethodsForm/PaymentMethodsForm.js';
import {
  createStripeSetupIntent,
  stripeCustomer,
} from '../../PaymentMethodsPage/PaymentMethodsPage.duck';

import css from './SubscriptionPlan.module.css';
import { currentUserSelector } from '../../../ducks/user.duck.js';
import { createSubscription } from './SubscriptionPage.duck.js';

import { showErrorToast } from '../../../util/toast.js';
import { SUBSCRIPTION_MODAL_STEPS } from '../../../util/enums.js';

const PaymentFormComponent = props => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardState, setCardState] = useState(null);
  const [saveCard, setSaveCard] = useState(false);
  const showCardCheckBox = true;

  const {
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    deletePaymentMethodInProgress,
    onCreateSetupIntent,
    onHandleCardSetup,
    onSavePaymentMethod,
    onDeletePaymentMethod,
    onCreateSubscription,
    fetchStripeCustomer,
    onManageDisableScrolling,
    intl,
    stripeCustomerFetched,
    selectedItem,
    handleCancel,
    setCurrentStep,
    couponCode,
  } = props;
  const currentUser = useSelector(currentUserSelector);
  const userId = currentUser?.id?.uuid || currentUser?.id;

  // couponData is stored in SubscriptionPage duck after redeemCoupon
  const { couponData } = useSelector(state => state?.SubscriptionPage || {});

  const getClientSecret = setupIntent => {
    return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
  };

  const getPaymentParams = (currentUser, formValues) => {
    const { name, addressLine1, addressLine2, postal, state, city, country } = formValues;
    const addressMaybe =
      addressLine1 && postal
        ? {
            address: {
              city: city,
              country: country,
              line1: addressLine1,
              line2: addressLine2,
              postal_code: postal,
              state: state,
            },
          }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    return {
      payment_method_data: {
        billing_details: billingDetails,
      },
    };
  };

  const handleSubmit = async params => {
    setIsSubmitting(true);
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const stripeCustomerObj = ensuredCurrentUser.stripeCustomer;
    const defaultPaymentMethodId =
      stripeCustomerObj?.defaultPaymentMethod?.attributes?.stripePaymentMethodId;

    const { stripe, card, formValues } = params;
    const payloadBase = {
      currency: selectedItem?.currency,
      priceId: selectedItem?.id || selectedItem?.priceId,
      discountCode: couponData?.id || null,
      userId: userId,
      productName: selectedItem?.productName || null,
      productId: selectedItem?.productId || null,
    };
    // Case 1: user already has a default payment method and is NOT replacing the card
    if (defaultPaymentMethodId && cardState !== 'replaceCard') {
      try {
        const payload = {
          ...payloadBase,
          paymentMethodId: defaultPaymentMethodId,
        };
        const response = await onCreateSubscription(payload);
        if (response?.statusCode === 200) {
          fetchStripeCustomer();
          setIsSubmitting(false);
          setCardState('default');
          setCurrentStep(SUBSCRIPTION_MODAL_STEPS?.STEP_3);
        } else {
          showErrorToast('Subscription creation failed. Please try again.');
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error(error);
        showErrorToast('Error in subscription creation. Please try again.');
        setIsSubmitting(false);
      }
      return;
    }

    // Case 2: no default payment method => create setup intent & save card
    onCreateSetupIntent()
      .then(setupIntent => {
        const stripeParams = {
          stripe,
          card,
          setupIntentClientSecret: getClientSecret(setupIntent),
          paymentParams: getPaymentParams(currentUser, formValues),
        };

        return onHandleCardSetup(stripeParams);
      })
      .then(result => {
        const newPaymentMethod = result.setupIntent.payment_method;

        // Save the payment method
        return onSavePaymentMethod(stripeCustomerObj, newPaymentMethod).then(() => {
          const payload = {
            ...payloadBase,
            paymentMethodId: newPaymentMethod,
          };

          // Create a subscription
          return onCreateSubscription(payload).then(response => {
            if (response?.statusCode === 200) {
              setCurrentStep(SUBSCRIPTION_MODAL_STEPS?.STEP_3);
            } else {
              showErrorToast('Subscription creation failed. Please try again.');
            }
          });
        });
      })
      .then(() => {
        fetchStripeCustomer();
        setIsSubmitting(false);
        setCardState('default');
      })
      .catch(error => {
        console.error(error);
        showErrorToast('An error occurred. Please try again.');
        setIsSubmitting(false);
      });
  };

  const handleRemovePaymentMethod = () => {
    onDeletePaymentMethod().then(() => {
      fetchStripeCustomer();
    });
  };

  const handleClose = () => {
    setCurrentStep(SUBSCRIPTION_MODAL_STEPS?.STEP_1);
    handleCancel();
  };

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!ensuredCurrentUser.id;

  const hasDefaultPaymentMethod =
    currentUser &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id;

  // Get name for Stripe payment form auto-fill
  const userName = currentUserLoaded
    ? `${ensuredCurrentUser.attributes.profile.firstName} ${ensuredCurrentUser.attributes.profile.lastName}`
    : null;

  const card = hasDefaultPaymentMethod
    ? ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).attributes.card
    : null;

  const initalValuesForStripePayment = { name: userName };
  const showForm = cardState === 'replaceCard' || !hasDefaultPaymentMethod;
  const showCardDetails = !!hasDefaultPaymentMethod;

  return (
    <div className={css.content}>
      {/* <H3 as="h1">
        <FormattedMessage id="PaymentMethodsPage.heading" />
      </H3> */}

      {isSubmitting ? (
        <div className={css.iconSpinner}>
          <IconSpinner />
        </div>
      ) : null}

      {showCardDetails ? (
        <>
          <SavedCardDetails
            card={card}
            onManageDisableScrolling={onManageDisableScrolling}
            onChange={setCardState}
            onDeleteCard={handleRemovePaymentMethod}
            deletePaymentMethodInProgress={deletePaymentMethodInProgress}
          />

          <PrimaryButton className={css.submitButton} type="submit" onClick={handleSubmit}>
            <FormattedMessage id="PaymentMethodsForm.submitPaymentInfo" />
          </PrimaryButton>
        </>
      ) : null}

      {showForm ? (
        <PaymentMethodsForm
          className={css.paymentForm}
          formId="PaymentMethodsForm"
          initialValues={initalValuesForStripePayment}
          onSubmit={handleSubmit}
          handleRemovePaymentMethod={handleRemovePaymentMethod}
          hasDefaultPaymentMethod={hasDefaultPaymentMethod}
          addPaymentMethodError={addPaymentMethodError}
          deletePaymentMethodError={deletePaymentMethodError}
          createStripeCustomerError={createStripeCustomerError}
          handleCardSetupError={handleCardSetupError}
          inProgress={isSubmitting}
          saveCard={saveCard}
          setSaveCard={setSaveCard}
          showCardCheckBox={showCardCheckBox}
        />
      ) : null}

      <SecondaryButton className={css.closeButton} onClick={handleClose}>
        {intl.formatMessage({ id: 'SubscriptionPage.SubscriptionPageModal.cancelButton' })}
      </SecondaryButton>
    </div>
  );
};

PaymentFormComponent.defaultProps = {
  currentUser: null,
  addPaymentMethodError: null,
  deletePaymentMethodError: null,
  createStripeCustomerError: null,
  handleCardSetupError: null,
  onCreateSubscription: null,
};

PaymentFormComponent.propTypes = {
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool.isRequired,
  addPaymentMethodError: object,
  deletePaymentMethodError: object,
  createStripeCustomerError: object,
  handleCardSetupError: object,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onDeletePaymentMethod: func.isRequired,
  fetchStripeCustomer: func.isRequired,
  onCreateSubscription: func.isRequired,
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = state.paymentMethods;

  const { stripeCustomerFetched } = state.PaymentMethodsPage;
  const { handleCardSetupError } = state.stripe;

  return {
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    stripeCustomerFetched,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onCreateSetupIntent: params => dispatch(createStripeSetupIntent(params)),
  onSavePaymentMethod: (stripeCustomer, newPaymentMethod) =>
    dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod)),
  onDeletePaymentMethod: params => dispatch(deletePaymentMethod(params)),
  onCreateSubscription: body => dispatch(createSubscription(body)),
});

const PaymentForm = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(PaymentFormComponent);

export default PaymentForm;
